import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { rsFromTo } from '../../shared/motion/rs-motion';

export type ShotIcon =
  | 'users'
  | 'monitor'
  | 'chat'
  | 'group'
  | 'star'
  | 'heart'
  | 'crown'
  | 'bolt';

export interface Shot {
  img: string;
  alt: string;
  title: string;
  /** One line, no more. */
  copy: string;
  icon: ShotIcon;
  /** Slot in the magazine grid. */
  slot: 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h';
}

let registered = false;

@Component({
  selector: 'app-team',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss'],
})
export class TeamComponent implements AfterViewInit, OnDestroy {
  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;
  @ViewChildren('card', { read: ElementRef })
  cardEls?: QueryList<ElementRef<HTMLElement>>;

  reduced = false;

  /**
   * The eight workplace photographs already in the project.
   * Captions replace the previous placeholder "Workplace Picture N".
   */
  readonly shots: Shot[] = [
    {
      img: 'assets/images/our-teams/(1).svg',
      alt: 'The ResilienceSoft team gathered together at an office celebration',
      title: 'Team Together',
      copy: 'One team, one direction.',
      icon: 'users',
      slot: 'a',
    },
    {
      img: 'assets/images/our-teams/(2).svg',
      alt: 'The ResilienceSoft open-plan development floor',
      title: 'Our Workspace',
      copy: 'Where the work gets made.',
      icon: 'monitor',
      slot: 'b',
    },
    {
      img: 'assets/images/our-teams/(3).svg',
      alt: 'Colleagues talking during an office event',
      title: 'Team Interactions',
      copy: 'Ideas move between people.',
      icon: 'chat',
      slot: 'c',
    },
    {
      img: 'assets/images/our-teams/(4).svg',
      alt: 'Team members in a collaborative meeting around a laptop',
      title: 'Collaborative Discussions',
      copy: 'Every voice counts.',
      icon: 'group',
      slot: 'd',
    },
    {
      img: 'assets/images/our-teams/(5).svg',
      alt: 'The team celebrating a milestone together',
      title: 'Celebrating Success',
      copy: 'We mark the wins together.',
      icon: 'star',
      slot: 'e',
    },
    {
      img: 'assets/images/our-teams/(6).svg',
      alt: 'Colleagues relaxing together at a team gathering',
      title: 'Team Bonding',
      copy: 'Colleagues first, friends too.',
      icon: 'heart',
      slot: 'f',
    },
    {
      img: 'assets/images/our-teams/(7).svg',
      alt: 'ResilienceSoft leadership team',
      title: 'Leadership',
      copy: 'Direction you can trust.',
      icon: 'crown',
      slot: 'g',
    },
    {
      img: 'assets/images/our-teams/(8).svg',
      alt: 'Developers focused at work on the office floor',
      title: 'Focused & Driven',
      copy: 'Deep work, every day.',
      icon: 'bolt',
      slot: 'h',
    },
  ];

  private ctx?: gsap.Context;
  private io?: IntersectionObserver;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackShot = (_i: number, s: Shot) => s.slot;

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => this.buildEntrance(), this.host.nativeElement);
      this.bindParallax();
      this.watchVisibility();
    });
  }

  /* ------------------------------------------------------------------
     ENTRY — header fades up, then the cards stagger in
     ------------------------------------------------------------------ */
  private buildEntrance(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power4.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    });

    tl.fromTo('.team__badge', ...rsFromTo({ opacity: 0, y: 20, duration: 0.7 }))
      .fromTo('.team__title', ...rsFromTo({ opacity: 0, y: 28, duration: 0.8 }), '-=0.44')
      .fromTo('.team__divider', ...rsFromTo({ opacity: 0, scaleX: 0, duration: 0.6 }), '-=0.52')
      .fromTo('.team__lede', ...rsFromTo({ opacity: 0, y: 18, duration: 0.7 }), '-=0.5');

    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    if (cards.length) {
      gsap.fromTo(cards, ...rsFromTo({
        opacity: 0,
        y: 80,
        scale: 0.95,
        duration: 0.8,
        ease: 'power4.out',
        stagger: 0.08,
        scrollTrigger: {
          trigger: this.grid!.nativeElement,
          start: 'top 88%',
          once: true,
        },
      }));
    }

    gsap.fromTo('.team__cta', ...rsFromTo({
      opacity: 0,
      y: 40,
      duration: 0.8,
      ease: 'power4.out',
      scrollTrigger: {
        trigger: '.team__cta',
        start: 'top 92%',
        once: true,
      },
    }));
  }

  /* ------------------------------------------------------------------
     PARALLAX — 3px to 6px per card, by depth. Applied to an inner
     wrapper so it never contends with the hover lift on the card.
     ------------------------------------------------------------------ */
  private bindParallax(): void {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const root = this.root?.nativeElement;
    if (!fine || !root) return;

    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    const movers = cards
      .map((c) => c.querySelector('.team__shift') as HTMLElement | null)
      .filter((el): el is HTMLElement => !!el)
      .map((el, i) => ({
        x: gsap.quickTo(el, 'x', { duration: 1, ease: 'power3.out' }),
        y: gsap.quickTo(el, 'y', { duration: 1, ease: 'power3.out' }),
        depth: 3 + (i % 4), // 3px .. 6px
      }));

    let queued = false;
    let nx = 0;
    let ny = 0;

    const apply = () => {
      queued = false;
      movers.forEach((m) => {
        m.x(nx * m.depth);
        m.y(ny * m.depth);
      });
    };

    const onMove = (e: MouseEvent) => {
      const r = root.getBoundingClientRect();
      nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onLeave = () => {
      nx = 0;
      ny = 0;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    root.addEventListener('mousemove', onMove, { passive: true });
    root.addEventListener('mouseleave', onLeave, { passive: true });
    this.cleanups.push(() => {
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseleave', onLeave);
    });
  }

  /* ------------------------------------------------------------------
     Ken Burns, the drifting blobs and the CTA pulse are CSS loops;
     this halts every one of them while the section is off screen.
     ------------------------------------------------------------------ */
  private watchVisibility(): void {
    const el = this.root?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    this.io = new IntersectionObserver(
      (entries) =>
        el.classList.toggle('is-paused', !entries.some((e) => e.isIntersecting)),
      { threshold: 0 }
    );

    this.io.observe(el);
    this.cleanups.push(() => this.io?.disconnect());
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.io?.disconnect();
    this.ctx?.revert();
  }
}
